import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Play, Coffee, RotateCcw, Square, Clock, Timer, AlertCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/api";
import { toast } from "sonner";

function fmtMin(m) {
  if (!m && m !== 0) return "—";
  const h = Math.floor(m / 60), mm = m % 60;
  return `${h}h${String(mm).padStart(2, "0")}`;
}

export default function Pointage() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [clock, setClock] = useState(new Date());

  const load = () => { api.get("/attendance/today").then((r) => setToday(r.data)); api.get("/attendance").then((r) => setHistory(r.data)); };
  useEffect(() => { load(); const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);

  const act = async (action) => {
    try { const { data } = await api.post("/attendance/action", { action }); setToday(data); toast.success("Pointage enregistré"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!today) return <Loader />;
  const status = today.status || "not_started";

  const buttons = [
    { action: "start_day", label: "Commencer ma journée", icon: Play, color: "bg-emerald-600 hover:bg-emerald-700", show: status === "not_started" },
    { action: "start_break", label: "Commencer la pause", icon: Coffee, color: "bg-amber-500 hover:bg-amber-600", show: status === "working" },
    { action: "resume", label: "Reprendre le travail", icon: RotateCcw, color: "bg-brand-600 hover:bg-brand-700", show: status === "on_break" },
    { action: "end_day", label: "Terminer ma journée", icon: Square, color: "bg-rose-600 hover:bg-rose-700", show: status === "working" },
  ];
  const statusLabel = { not_started: "Journée non commencée", working: "En activité", on_break: "En pause", finished: "Journée terminée" }[status];

  return (
    <div>
      <PageHeader title="Pointage" subtitle="Enregistrement du temps de travail" />

      <div className="max-w-md mx-auto lg:mx-0 lg:max-w-lg">
        <div className="rounded-2xl border bg-white p-8 text-center shadow-sm">
          <p className="text-xs uppercase font-mono tracking-widest text-slate-400">{fmtDate(new Date().toISOString())}</p>
          <p className="font-mono text-5xl font-bold text-slate-900 mt-2 tabular">{clock.toLocaleTimeString("fr-FR")}</p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1.5">
            <span className={`h-2 w-2 rounded-full ${status === "working" ? "bg-emerald-500 animate-pulse" : status === "on_break" ? "bg-amber-500" : status === "finished" ? "bg-slate-400" : "bg-slate-300"}`} />
            <span className="text-sm font-semibold text-slate-700">{statusLabel}</span>
          </div>

          <div className="grid grid-cols-1 gap-3 mt-8">
            {buttons.filter((b) => b.show).map((b) => (
              <button key={b.action} onClick={() => act(b.action)} data-testid={`pointage-${b.action}`}
                className={`flex items-center justify-center gap-2 rounded-xl py-4 text-white font-semibold text-lg transition-transform active:scale-95 ${b.color}`}>
                <b.icon className="h-5 w-5" />{b.label}
              </button>
            ))}
            {status === "finished" && <p className="text-sm text-slate-500 py-4">Merci, votre journée est enregistrée. À demain 👋</p>}
          </div>

          {today.events?.length > 0 && (
            <div className="mt-6 pt-6 border-t grid grid-cols-3 gap-3 text-center">
              <div><Clock className="h-4 w-4 mx-auto text-brand-500 mb-1" /><p className="text-[10px] text-slate-400 uppercase">Travaillé</p><p className="font-mono font-bold text-sm">{fmtMin(today.temps_travaille_min)}</p></div>
              <div><Coffee className="h-4 w-4 mx-auto text-amber-500 mb-1" /><p className="text-[10px] text-slate-400 uppercase">Pause</p><p className="font-mono font-bold text-sm">{fmtMin(today.temps_pause_min)}</p></div>
              <div><Timer className="h-4 w-4 mx-auto text-emerald-500 mb-1" /><p className="text-[10px] text-slate-400 uppercase">Heures sup.</p><p className="font-mono font-bold text-sm">{fmtMin(today.heures_sup_min)}</p></div>
            </div>
          )}
        </div>
      </div>

      <h3 className="font-head font-semibold text-lg mt-8 mb-3">Historique de pointage</h3>
      <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead>Début</TableHead><TableHead>Fin</TableHead><TableHead>Travaillé</TableHead><TableHead>Pause</TableHead><TableHead>Retard</TableHead><TableHead>H. sup.</TableHead></TableRow></TableHeader>
          <TableBody>
            {history.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Aucun historique</TableCell></TableRow> :
              history.map((a) => (
                <TableRow key={a.id || a.date}>
                  <TableCell className="font-medium">{fmtDate(a.date)}</TableCell>
                  <TableCell className="font-mono text-xs">{a.heure_debut || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{a.heure_fin || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtMin(a.temps_travaille_min)}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtMin(a.temps_pause_min)}</TableCell>
                  <TableCell className={`font-mono text-xs ${a.retard_min > 0 ? "text-rose-600" : ""}`}>{a.retard_min ? fmtMin(a.retard_min) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{a.heures_sup_min ? fmtMin(a.heures_sup_min) : "—"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div></div>
    </div>
  );
}
